/**
 * Small pill components used inside the Documents table.
 *
 * Extracted from DocumentsTable.jsx in the Feb-25 refactor — same
 * behaviour, just lifted into its own file to keep DocumentsTable
 * focused on the table shell + filtering.
 *
 *   • DocStatePill     — pill for "Uploaded / Pending / Failed / N/A".
 *   • ExtractionPill   — pill for the "Check" column (plain status
 *                          + classifier-vs-slot mismatch).
 */
import React from "react";
import { AlertCircle, CheckCircle2, Clock, MinusCircle, Sparkles, ShieldQuestion } from "lucide-react";
import { plainDocKey, plainDocType, plainExtractionStatus } from "../../../lib/plainLanguage";


export function DocStatePill({ state }) {
  const cfg = {
    uploaded:  { label: "Uploaded", cls: "border-cyan-400/30 bg-cyan-400/[0.06] text-cyan-100", icon: CheckCircle2 },
    na:        { label: "Not applicable", cls: "border-white/[0.10] bg-white/[0.04] text-zinc-300", icon: MinusCircle },
    pending:   { label: "Pending",  cls: "border-amber-400/30 bg-amber-400/[0.06] text-amber-100", icon: Clock },
    failed:    { label: "Failed",   cls: "border-rose-400/30 bg-rose-400/[0.06] text-rose-100", icon: AlertCircle },
  }[state] || { label: state || "—", cls: "border-white/[0.10] bg-white/[0.04] text-zinc-300", icon: MinusCircle };
  const Icon = cfg.icon;
  return <span className={`inline-flex items-center gap-1 rounded-full border ${cfg.cls} px-2 py-0.5 text-[11px]`}><Icon className="h-3 w-3" /> {cfg.label}</span>;
}


/** Brief 3.13 Phase 2 (Feb 25 2026) — Claude semantic verdict pill.
 *
 *   - "verified"     → green ✓  "Claude verified"
 *   - "issues_found" → amber ⚠  "Claude flagged · N" (with finding count)
 *   - "inconclusive" → grey  ?  "Claude unsure"
 *   - null/missing   → tiny "Reviewing…" spinner that fades out once the
 *                       background semantic review finishes (~5-10s).
 *
 * AM manual verification overrides the Claude verdict — when status is
 * `manually_verified`, we DON'T render this pill (the row already shows
 * the AM stamp). The Claude reasoning + findings remain accessible in
 * the row's expander so the audit trail is preserved.
 */
export function ClaudeVerdictPill({ verdict, findings, manuallyVerified, reviewing }) {
  if (manuallyVerified) return null;
  if (!verdict && reviewing) {
    return (
      <span
        data-testid="claude-verdict-pill-reviewing"
        className="inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-white/[0.02] text-zinc-400 px-2 py-0.5 text-[10.5px]"
        title="Claude is cross-checking this document against the form and the other uploaded docs."
      >
        <Sparkles className="h-2.5 w-2.5 animate-pulse" />
        Claude reviewing…
      </span>
    );
  }
  if (!verdict) return null;
  const findingCount = Array.isArray(findings) ? findings.length : 0;
  const cfg = {
    verified: {
      label: "Claude verified",
      cls: "border-emerald-400/30 bg-emerald-400/[0.06] text-emerald-200",
      Icon: CheckCircle2,
    },
    issues_found: {
      label: findingCount > 0 ? `Claude flagged · ${findingCount}` : "Claude flagged",
      cls: "border-amber-400/35 bg-amber-400/[0.08] text-amber-200",
      Icon: AlertCircle,
    },
    inconclusive: {
      label: "Claude unsure",
      cls: "border-white/[0.10] bg-white/[0.03] text-zinc-300",
      Icon: ShieldQuestion,
    },
  }[verdict] || {
    label: verdict, cls: "border-white/[0.10] bg-white/[0.03] text-zinc-300",
    Icon: ShieldQuestion,
  };
  const Icon = cfg.Icon;
  return (
    <span
      data-testid={`claude-verdict-pill-${verdict}`}
      className={`inline-flex items-center gap-1 rounded-full border ${cfg.cls} px-2 py-0.5 text-[10.5px]`}
      title={findingCount > 0
        ? `Claude raised ${findingCount} concern${findingCount === 1 ? "" : "s"}. See row details.`
        : "Claude reviewed against the form and other uploaded docs."}
    >
      <Icon className="h-2.5 w-2.5" />
      {cfg.label}
    </span>
  );
}


export function ExtractionPill({ status, classified, expected }) {
  if (!status) return <span className="text-zinc-500 text-[11.5px]">—</span>;
  const info = plainExtractionStatus(status);
  const tone =
    info.tone === "ok" ? "text-emerald-300" :
    info.tone === "warn" ? "text-amber-300" :
    info.tone === "bad" ? "text-rose-300" : "text-zinc-400";
  const classifiedPlain = classified ? plainDocType(classified) : null;
  const expectedPlain = expected ? plainDocKey(expected) : null;
  // The "doc type mismatch" warning hides once an AM has manually
  // verified the row — the audit trail still records what the AI
  // thought (in the underlying classified field), but the visible
  // pill follows the AM's override.
  const isManuallyVerified = status === "manually_verified";
  const mismatch = !isManuallyVerified && classified && expected && classified !== expected;
  return (
    <div className="text-[11.5px]">
      <span className={tone} data-testid="extraction-pill-status">{info.label}</span>
      {classifiedPlain && (
        <div className="mt-0.5 text-zinc-500" data-testid="extraction-pill-classified">
          {mismatch
            ? <span className="text-amber-300">{classifiedPlain} (expected {expectedPlain})</span>
            : classifiedPlain}
        </div>
      )}
    </div>
  );
}
